import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, PauseCircle, XCircle, Undo2, PlayCircle, Loader2, RefreshCcw } from "lucide-react";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";
import { Button } from "@/components/ui/button";
import { fetchCancelSaveAnalytics } from "@/lib/api";
import { toast } from "sonner";

const CARDS = [
  { key: "paused",    label: "Paused",    icon: PauseCircle, tone: "text-terracotta border-terracotta/40 bg-terracotta/10", note: "chose to pause instead of cancel" },
  { key: "cancelled", label: "Cancelled", icon: XCircle,     tone: "text-bone-300 border-obsidian-600",                   note: "left after off-ramp" },
  { key: "refunded",  label: "Refunded",  icon: Undo2,       tone: "text-bone-300 border-obsidian-600",                   note: "took the 7-day money-back" },
  { key: "resumed",   label: "Resumed",   icon: PlayCircle,  tone: "text-terracotta border-terracotta/40 bg-terracotta/10", note: "came back from pause" },
];

/**
 * AdminCancelSave — off-ramp analytics. Shows how many journalers chose
 * each off-ramp path (pause / cancel / refund / resume) so operator can
 * see whether the "save" flow is doing its job.
 */
export default function AdminCancelSave() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const r = await fetchCancelSaveAnalytics();
      setData(r);
    } catch (e) {
      console.error(e);
      toast.error("Could not load cancel-save analytics.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const savePct = data ? Math.round((data.save_ratio || 0) * 100) : 0;

  return (
    <div className="min-h-screen">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-obsidian-600">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-200 transition-colors"
          data-testid="back-to-admin"
        >
          <ChevronLeft className="h-3 w-3" /> Admin
        </Link>
        <div className="flex items-center gap-4">
          <AdminToggle />
          <UserBadge />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-10 sm:pt-16 pb-24">
        <p className="font-sans uppercase tracking-[0.24em] sm:tracking-[0.28em] text-xs text-terracotta mb-3 sm:mb-4">
          Umbra · Cancel-Save
        </p>
        <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-bone-100 leading-tight">
            Off-ramp analytics.
          </h1>
          <Button
            onClick={load}
            disabled={refreshing}
            variant="outline"
            data-testid="cancel-save-refresh"
            className="rounded-none border-obsidian-600 hover:border-terracotta bg-transparent text-bone-200 hover:text-terracotta hover:bg-transparent font-sans uppercase tracking-wider text-xs px-4 py-3 min-h-[44px] transition-colors"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCcw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>
        <p className="font-serif italic text-base sm:text-lg text-bone-400 max-w-2xl leading-relaxed mb-10">
          Every time a journaler taps toward cancel, we offer them a gentler
          path first. This shows how often that path holds.
        </p>

        {loading ? (
          <div className="flex items-center gap-3 p-8 border border-obsidian-600">
            <Loader2 className="h-4 w-4 animate-spin text-terracotta" />
            <p className="font-sans text-bone-400">Reading the events log…</p>
          </div>
        ) : (
          <>
            {/* Save ratio */}
            <div
              data-testid="cancel-save-headline"
              className="border border-terracotta/40 bg-obsidian-800 p-5 sm:p-6 mb-8"
            >
              <p className="font-sans uppercase tracking-[0.22em] text-[0.65rem] text-bone-500 mb-2">Save ratio</p>
              <p className="font-serif text-4xl sm:text-5xl text-terracotta leading-none">
                {savePct}%
              </p>
              <p className="font-serif italic text-bone-400 mt-3 leading-relaxed">
                Of {data.total_off_ramp_touches} journalers who reached the off-ramp,
                {" "}{data.paused} chose the gentler pause instead of cancelling or refunding.
              </p>
              <div className="mt-4 h-2 bg-obsidian-900 relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-terracotta transition-all"
                  style={{ width: `${savePct}%` }}
                />
              </div>
            </div>

            {/* Four cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="cancel-save-cards">
              {CARDS.map((c) => {
                const Icon = c.icon;
                return (
                  <div
                    key={c.key}
                    data-testid={`cancel-save-card-${c.key}`}
                    className={`border p-4 ${c.tone.replace("text-", "")} bg-obsidian-800`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <Icon className={`h-4 w-4 ${c.tone.split(" ")[0]}`} />
                      <p className="font-sans uppercase tracking-[0.22em] text-[0.55rem] text-bone-500">
                        {c.label}
                      </p>
                    </div>
                    <p className={`font-serif text-3xl leading-tight ${c.tone.split(" ")[0]}`}>
                      {data[c.key] || 0}
                    </p>
                    <p className="font-sans text-[0.65rem] uppercase tracking-[0.18em] text-bone-500 mt-2 leading-relaxed">
                      {c.note}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Empty state note */}
            {data.total_off_ramp_touches === 0 && (
              <div className="border border-obsidian-600 p-8 text-center">
                <p className="font-serif italic text-bone-300 mb-2">The off-ramp is quiet.</p>
                <p className="font-sans text-sm text-bone-500">
                  Analytics will appear here the first time a journaler pauses, cancels, or asks for a refund.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
